import { KeyOffSharp, KeySharp } from '@mui/icons-material';
import { Box, Card, CardContent, InputLabel, Link, MenuItem, Select, SelectChangeEvent, SelectProps, Stack, SxProps, Tooltip, Typography } from '@mui/material';
import { t } from 'i18next';
import { ReactNode, useState } from 'react';
import { BlankCell } from '../../../common/components/Cells/BlankCell';
import { TraceType, getTraceTypeTracer } from '../../../common/enums/TraceType';
import { DateUtils } from '../../../common/utils/DateUtils';
import { Report, ReportDone } from '../../common/models/Report';
import { ReportDetailsSkeleton } from './skeletons/ReportDetailsSkeleton';
import { useTranslation } from 'react-i18next';
import { ComponentLoader } from '../../../common/components/ComponentLoader';
import { useParams } from 'react-router-dom';

type RowProps = {
  label: string;
  value: ReactNode;
  sx?: SxProps;
};

const Row = ({ label, value, sx }: RowProps) => {
  return (
    <Typography data-role="report-details-row" variant="body1" component="div" sx={{ display: 'flex', mb: 1, ...(sx || {}) }}>
      <Typography sx={{ whiteSpace: 'nowrap', fontWeight: 700, pr: 0.75 }}>{label}:</Typography>
      {value}
    </Typography>
  );
};

type TraceProps = {
  traceName: string;
  traceType: TraceType | null;
  salt: string | null;
};

const TraceMeta = ({ traceName, traceType, salt }: TraceProps) => {
  if (!traceType) {
    return <>{traceName}</>;
  }
  if (salt) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={t('reportGeneratedWithProductionSafeTool', { tool: getTraceTypeTracer(traceType) })}>
          <KeySharp sx={{ p: 0.25, borderRadius: '50%', backgroundColor: 'grey.300' }} />
        </Tooltip>
        <Typography component="span" sx={{ pl: 0.5 }}>
          {traceName}
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title={t('reportGeneratedWithStandardTool', { tool: getTraceTypeTracer(traceType) })}>
        <KeyOffSharp sx={{ p: 0.25, borderRadius: '50%', backgroundColor: 'grey.300' }} />
      </Tooltip>
      <Typography component="span" sx={{ pl: 0.5 }}>
        {traceName}
      </Typography>
    </Box>
  );
};

type Props = {
  isStaticInfo?: boolean;
  report: Report | null;
  initialLoad: boolean;
  isLoading?: boolean;
  handleVersionChange: (id: number) => void;
  profileLink: (report: Report) => string;
};

type VersionSelectOption = {
  formattedDate: string;
  formattedName: string;
  id: number;
};

export const ReportDetails = (props: Props) => {
  const { report, isLoading, initialLoad, profileLink, isStaticInfo, handleVersionChange } = props;
  const [versionOpen, setVersionOpen] = useState(false);
  const { t } = useTranslation();
  const { reportId: currentReportId } = useParams();

  if (initialLoad) {
    return <ReportDetailsSkeleton />;
  }

  if (!report) return null;

  const trace = report.getTrace();
  const profile = report.getProfile();
  const versions = report.getVersions().filter((v) => v.getRuleset() === report.getRuleset());
  const nonsensitiveSalt = report instanceof ReportDone ? report.getNonsensitiveSalt() : null;
  const versionMap: Map<number, VersionSelectOption> = new Map(
    versions.map((v) => {
      const formattedDate = DateUtils.toUTCTimeString(v.getDate());
      return [v.getId(), { formattedDate, formattedName: `${formattedDate} (${v.getName()})`, id: v.getId() }];
    })
  );

  const labelA11yId = 'version-selector-label';
  const versionA11yProps: Partial<SelectProps<number>> = {
    labelId: labelA11yId,
    id: 'version-selector-input',
    SelectDisplayProps: versionOpen ? { 'aria-expanded': true, 'aria-controls': 'version-selector-menu' } : { 'aria-expanded': false, 'aria-controls': '' },
    MenuProps: { MenuListProps: { id: 'version-selector-menu' } },
    onOpen: () => setVersionOpen(true),
    onClose: () => setVersionOpen(false),
  };

  const onChange = (event: SelectChangeEvent<number>) => {
    const id = event.target.value as number;
    handleVersionChange(id);
  };

  return (
    <ComponentLoader loading={isLoading ?? false}>
      <Card data-role="report-details" sx={{ backgroundColor: isStaticInfo ? '#F1F8FF' : null }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 1 }}>
          <Typography variant="h5" component="p" sx={{ py: 1 }}>
            {report.getName()}
          </Typography>
          {isStaticInfo && (
            <Typography
              sx={{
                borderRadius: '0.25rem',
                backgroundColor: '#0078BB14',
                p: 1,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                fontWeight: 500,
                color: 'primary.main',
              }}
            >
              {t('comparison.latest')}
            </Typography>
          )}
        </Stack>
        <CardContent>
          {isStaticInfo ? (
            <Row label={t('version')} value={versionMap.get(report.getId())?.formattedDate} />
          ) : (
            <Typography
              data-role="report-details-row"
              variant="body1"
              component="div"
              sx={{ display: 'flex', mb: 0.5, alignItems: 'center', ...(trace === null ? { mb: 0 } : {}) }}
            >
              <InputLabel id={labelA11yId} sx={{ whiteSpace: 'nowrap', fontWeight: 700, pr: 0.75, color: 'inherit' }}>
                {t('version')}:
              </InputLabel>
              <Select
                variant="standard"
                value={report.getId()}
                sx={{ overflow: 'hidden', '& .MuiSelect-select': { py: 0.25 } }}
                renderValue={(versionId: number) => versionMap.get(versionId)?.formattedDate || ''}
                onChange={onChange}
                {...versionA11yProps}
              >
                {Array.from(versionMap, ([id, version]) =>
                  id.toString() !== currentReportId ? (
                    <MenuItem key={id} value={id}>
                      {version.formattedName}
                    </MenuItem>
                  ) : null
                )}
              </Select>
            </Typography>
          )}

          {trace !== null && (
            <Row
              label={t('traceUsed')}
              sx={{ mb: 0 }}
              value={<TraceMeta traceName={trace.getName()} traceType={report.getTraceType() ?? null} salt={nonsensitiveSalt} />}
            />
          )}
          <Row
            label={t('profileUsed')}
            sx={{ my: 1 }}
            value={
              profile ? (
                <Link href={profileLink(report)}>{profile.getLabel()}</Link>
              ) : (
                <BlankCell blankText={t('noProfileInReport')} tooltip={t('noProfileInReportTooltip')} />
              )
            }
          />
        </CardContent>
      </Card>
    </ComponentLoader>
  );
};
